import { Box, IconButton, Typography, styled } from '@mui/material'
import { FiLoader } from 'react-icons/fi'
export const ChatInputWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '900px',
  margin: '0 auto',
  marginBottom: '16px',
}))

export const FormContainer = styled('form', {
  shouldForwardProp: prop => prop !== 'isDragging',
})(({ theme, isDragging }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: isDragging ? `${theme.palette.primary.main}08` : '#ffffff',
  borderRadius: '12px',
  border: '1px solid',
  borderColor: isDragging ? theme.palette.primary.main : '#e0e0e0',
  borderStyle: isDragging ? 'dashed' : 'solid',
  borderWidth: isDragging ? '2px' : '1px',
  transition: 'all 0.2s ease',
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: isDragging ? 'none' : `0 0 0 3px ${theme.palette.primary.main}15`,
  },
}))

export const DropOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: `${theme.palette.primary.main}20`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.2rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  borderRadius: '12px',
  zIndex: 10,
  pointerEvents: 'none',
}))

export const ChatTextarea = styled('textarea')(({ theme }) => ({
  width: 'calc(100% - 48px)',
  minHeight: '36px',
  border: 'none',
  outline: 'none',
  resize: 'none',
  background: 'transparent',
  fontSize: '15px',
  lineHeight: '1.6',
  paddingLeft: '36px',
  marginBottom: '16px',
  marginTop: '24px',
  color: '#1e293b',
  fontFamily: 'inherit',
  fontWeight: 400,
  display: 'block',
  marginInline: 'auto',
  '&:focus-visible': {
    outline: 'none !important',
    boxShadow: 'none !important',
    border: 'none !important',
  },
  '&::placeholder': {
    color: '#94a3b8',
    fontWeight: 400,
  },
  '&:disabled': {
    backgroundColor: 'transparent',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
}))

export const BottomWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 12px',
  backgroundColor: '#f8f9fb',
  borderTop: '1px solid #e0e0e0',
  borderBottomLeftRadius: '12px',
  borderBottomRightRadius: '12px',
}))

export const ActionButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
}))

export const TextareaIconsRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
}))

export const IconSmallButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  fontSize: '1.2rem',
  width: 44,
  height: 44,
  borderRadius: 8,
  '& .MuiSvgIcon-root': {
    fontSize: 'inherit',
  },
}))

export const EnhanceButton = styled(IconSmallButton)(({ theme }) => ({
  color: theme.palette.warning.main,
  '&:hover': {
    backgroundColor: `${theme.palette.warning.main}15`,
  },
  '&:disabled': {
    color: theme.palette.action.disabled,
  },
}))

export const SendButton = styled(IconSmallButton)(({ theme }) => ({
  width: '44px',
  height: '44px',
  minWidth: 'unset',
  padding: 0,
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  transition: '.4s',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    opacity: 0.8,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}))

export const StopButton = styled(IconSmallButton)(({ theme }) => ({
  width: '44px',
  height: '44px',
  minWidth: 'unset',
  padding: 0,
  backgroundColor: theme.palette.error.main,
  color: '#fff',
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}))

export const FileInputLabel = styled('label')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: '1.2rem',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

export const FilterPopoverContent = styled(Box)(({ theme }) => ({
  padding: '16px',
  minWidth: '280px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}))

export const PopoverTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '8px',
}))

export const ToggleItemRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  cursor: 'pointer',
  fontSize: '14px',
  userSelect: 'none',
  padding: '8px',
  borderRadius: '6px',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

export const ToggleLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  color: theme.palette.text.primary,
}))

export const SwitchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '40px',
  height: '20px',
  backgroundColor: '#ccc',
  borderRadius: '10px',
  transition: 'background-color 0.3s',
  cursor: 'pointer',
  '& input': {
    display: 'none',
  },
  '& input:checked + .knob': {
    transform: 'translateX(20px)',
    backgroundColor: '#fff',
  },
  '& input:checked ~ &': {
    backgroundColor: theme.palette.primary.main,
  },
}))

export const Knob = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: '2px',
  left: '2px',
  width: '16px',
  height: '16px',
  backgroundColor: '#fff',
  borderRadius: '50%',
  transition: 'transform 0.3s',
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
}))

export const TokenUsageDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 12px',
  backgroundColor: theme.palette.grey[100],
  borderRadius: '4px',
  fontSize: '13px',
  color: theme.palette.text.secondary,
}))

export const SpinnerIcon = styled(FiLoader)({
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
})
